import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { SelectedContext } from "@/components/ai/prompt-input/NewPromptInput";

/**
 * Extracts a context URL from a SelectedContext for authentication purposes.
 * Returns the URL that should be used with useCheckSCMAuthentication.
 *
 * @param context - The selected context to extract URL from
 * @returns The context URL string, or empty string if no URL is applicable
 */
export function getContextUrlFromSelectedContext(context: SelectedContext): string {
    switch (context.kind) {
        case "pr":
        case "issue":
        case "repo":
        case "git-tag":
        case "git-commit":
            return context.url;
        case "project": {
            // For projects, we need to extract the git remote URL from the initializer
            return getRepoUrlFromInitializer(context.project.initializer) ?? "";
        }
        case "scratch":
            // Scratch environments don't have a context URL
            return "";
        default:
            return "";
    }
}

/**
 * Checks if two contexts are for the same repository.
 *
 * @param context1 - The first context to compare
 * @param context2 - The second context to compare
 * @returns True if the contexts are for the same repository, false otherwise
 */
export function isSameRepository(
    context1: SelectedContext,
    context2: { ownerName: string; repoName: string; url?: string },
) {
    if (context1.kind === "project" || context1.kind === "scratch") {
        return false;
    }

    // Basic owner/repo comparison
    const sameOwnerRepo = context1.ownerName === context2.ownerName && context1.repoName === context2.repoName;

    // If we have URLs, also compare hosts to avoid conflicts between different SCM providers
    if (sameOwnerRepo && context2.url && "url" in context1) {
        try {
            const host1 = new URL(context1.url).hostname;
            const host2 = new URL(context2.url).hostname;
            return host1 === host2;
        } catch {
            // If URL parsing fails, fall back to basic comparison
            return sameOwnerRepo;
        }
    }

    return sameOwnerRepo;
}
